/**
 * Dexie database for PowderLog — local-first storage.
 *
 * Canonical pattern: shared-docs/offline-first-architecture.md
 *
 * Tables:
 *   sessions     — completed ski sessions (stamped with userId for anon-vs-authed scoping)
 *   tracks       — GPS point arrays, stored separately because they're large
 *   mutationQueue — durable queue of {entity, op, recordId, payload} entries
 *   meta         — single-row key/value bag (anon UUID, lastSyncAt, activeSession snapshot,
 *                  banner dismissal state)
 */

import Dexie from 'dexie';

export const db = new Dexie('powderlog');

db.version(1).stores({
  // Sessions: primary key id, plus indexed userId + syncStatus + startTime + deletedAt
  sessions: 'id, userId, syncStatus, startTime, deletedAt, lastUpdatedAt',
  // Tracks: keyed by sessionId so we can pull/replace independently
  tracks: 'sessionId, userId',
  mutationQueue: '++id, status, entity, createdAt',
  meta: 'key',
});

export const SYNC_STATUS = Object.freeze({
  LOCAL: 'local',
  SYNCING: 'syncing',
  SYNCED: 'synced',
  ERROR: 'error',
});

/**
 * Wipe ALL local Dexie tables. Used by sign-out and the "Clear Local
 * Data" affordance for anonymous users. Preserves nothing — callers must
 * regenerate the anon UUID via getOrCreateAnonUserId() on the next read.
 */
export async function clearAllLocalData() {
  await db.transaction('rw', db.sessions, db.tracks, db.mutationQueue, db.meta, async () => {
    await Promise.all([
      db.sessions.clear(),
      db.tracks.clear(),
      db.mutationQueue.clear(),
      db.meta.clear(),
    ]);
  });
}

export default db;
